import { useEffect, useSyncExternalStore } from 'react';
import { inputSettled } from './advanceHelpers';

type Table = 'revenue' | 'orders';
interface TableData {
  items: { id: string; name: string }[];
  ready: boolean;
  categories?: { id: string; name: string }[];
  search: string;
}
const tables: Partial<Record<Table, TableData>> = {};
const examples: Partial<Record<Table, { id: string; item: string; position: string }>> = {};
const listeners = new Set<() => void>();
let revision = 0;
const emit = () => { revision += 1; listeners.forEach(listener => listener()); };
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

/** Publish the page's actual sorted rows, including rows beyond the first table page. */
export function useTourTableData(table: Table, data: TableData) {
  useEffect(() => {
    if (JSON.stringify(tables[table]) === JSON.stringify(data)) return;
    tables[table] = data;
    emit();
  }, [table, data]);
  useEffect(() => () => { delete tables[table]; emit(); }, [table]);
}

export function useTourTableRevision() {
  return useSyncExternalStore(subscribe, () => revision, () => revision);
}

export const hasTourTableItems = (table: Table) => Boolean(tables[table]?.items.length);
export const isTourTableReady = (table: Table) => tables[table]?.ready === true;
/** The page owns search matching; this only marks the frozen row among its filtered results. */
export const isTourSearchResult = (table: Table, id: string, search: string) => {
  const example = examples[table];
  return Boolean(example && example.id === id && search.trim());
};

export const revenueTourCategories = () => tables.revenue?.categories ?? [];

/** Freeze the requested row before typing changes the table's order and contents. */
export function tableSearchExercise(table: Table, position: number, anchor: string) {
  const settled = inputSettled(anchor, { changedFromInitial: false });
  const example = () => examples[table];
  const capture = () => {
    if (!isTourTableReady(table)) return;
    const items = tables[table]?.items ?? [];
    const index = Math.min(position, items.length) - 1;
    examples[table] = index >= 0 ? { id: items[index].id, item: items[index].name, position: String(index + 1) } : undefined;
  };
  return {
    reset: () => { delete examples[table]; emit(); },
    restoreSearch: () => {
      if (example()) window.dispatchEvent(new CustomEvent(`onboarding:${table}-search`, { detail: example()!.item }));
    },
    isAvailable: () => !isTourTableReady(table) || hasTourTableItems(table),
    hasExample: () => Boolean(example()),
    onEnter: () => {
      if (!example() || tables[table]?.search !== example()!.item) {
        delete examples[table];
        capture();
      }
      emit();
      const cleanup = settled.onEnter();
      // Data may finish loading after the step starts.
      const unsubscribe = subscribe(() => { if (!example()) capture(); });
      return () => { cleanup?.(); unsubscribe(); };
    },
    values: (): Record<string, string> => example() ? { context: 'example', item: example()!.item, position: example()!.position } : { context: '' },
    advanceWhen: () => (isTourTableReady(table) && !hasTourTableItems(table) && !example()) || Boolean(example() && settled.advanceWhen()
      && isTourSearchResult(table, example()!.id, tables[table]?.search ?? '')
      && tables[table]?.items.some(item => item.id === example()!.id)),
  };
}
